import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE, isDev } from '../config';
import { isIOS, isAndroid, isMobile, getRecordsConnectionInstructions, getHHSAppStoreUrl } from '../utils/platformDetect';

// Sources that support an on-demand pull. Apple Health Records is PUSH-only (the
// HealistHealthSync companion app writes to us every ~2h), so a "Sync Now" button there
// would have nothing to call. Keep this in step with the backend: there is no generic
// /api/health-records/sync — the Particle Health pull was the only implementation and it
// was removed 2026-10-02.
const SYNCABLE_SOURCES = { medicare_bb2: `${API_BASE}/api/medicare-bb2/sync` };

const PATHS = [
  {
    id: 'mobile-health',
    icon: '\uD83D\uDCF1',
    title: 'Sync from Mobile Health Platform',
    subtitle: 'via HealistHealthSync',
    desc: 'Import clinical records (conditions, medications, labs, immunizations, procedures, allergies) from hospitals and clinics connected to your mobile health platform.',
    action: 'view-mobile-health',
    comingSoon: true,
  },
  {
    id: 'medicare',
    icon: '\uD83C\uDFDB\uFE0F',
    title: 'Connect Medicare',
    desc: 'Import your Medicare claims history.',
    action: 'view-medicare',
    comingSoon: true,
  },
  {
    id: 'upload',
    icon: '\uD83D\uDCC4',
    title: 'Upload Medical Records',
    desc: 'Upload PDFs, lab results, or images. AI extracts conditions, medications, and lab values.',
    action: 'view-upload',
  },
];

export default function HealthRecordsHub({ fetchWithAuth }) {
  const [activeView, setActiveView] = useState('hub');
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingId, setSyncingId] = useState(null);
  const [viewRecordsConn, setViewRecordsConn] = useState(null);
  const [viewRecordsData, setViewRecordsData] = useState(null);
  const [viewRecordsLoading, setViewRecordsLoading] = useState(false);
  const [expandedRecords, setExpandedRecords] = useState(new Set());
  const [allExpanded, setAllExpanded] = useState(false);

  // The national-network provider search (Particle Health) was removed 2026-10-02 along
  // with the demographics form, consent block and polling that drove it. Clinical records
  // arrive via b.well, HealistHealthSync (Apple Health / Google Health Connect), Medicare
  // Blue Button, and manual upload.

  const loadConnections = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/health-records/connections`);
      if (res.ok) {
        const data = await res.json();
        setConnections(data.connections || []);
      }
    } catch (err) {
      console.error('Failed to load connections:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  useEffect(() => {
    const handleMessage = (e) => {
      if (e.data?.type === 'medicare-bb2-connected') {
        if (e.data.success) {
          loadConnections();
          setActiveView('hub');
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [loadConnections]);

  const handleSync = async (connId, sourceType) => {
    const endpoint = SYNCABLE_SOURCES[sourceType];
    if (!endpoint) return;  // button is not rendered for these; belt-and-braces
    setSyncingId(connId);
    try {
      const res = await fetchWithAuth(endpoint, { method: 'POST' });
      if (res.ok) {
        setTimeout(() => loadConnections(), 3000);
      }
    } catch (err) {
      console.error('Sync failed:', err);
    } finally {
      setSyncingId(null);
    }
  };

  const [disconnectConn, setDisconnectConn] = useState(null);
  const [disconnectOption, setDisconnectOption] = useState('retain');

  const executeDisconnect = async () => {
    if (!disconnectConn) return;
    const deleteData = disconnectOption === 'delete';
    try {
      const url = `${API_BASE}/api/health-records/disconnect`
        + `?connection_id=${disconnectConn.id}${deleteData ? '&delete_data=true' : ''}`;
      const res = await fetchWithAuth(url, { method: 'POST' });
      if (res.ok) {
        setDisconnectConn(null);
        loadConnections();
      }
    } catch (err) {
      console.error('Disconnect failed:', err);
    }
  };

  // Phase 2: Portal provider search
  // handlePortalSearch — REMOVED (FH deprecated May 2026)
  // handlePortalConnect — REMOVED (FH deprecated May 2026)

  const handleMedicareConnect = async () => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/medicare-bb2/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.redirect_url) {
          window.open(data.redirect_url, 'medicare-connect', 'width=600,height=700');
        }
      }
    } catch (err) {
      console.error('Medicare connect failed:', err);
    }
  };

  const handleViewRecords = async (conn) => {
    setViewRecordsConn(conn);
    setViewRecordsLoading(true);
    try {
      const endpoint = `${API_BASE}/api/health-records/records?connection_id=${conn.id}`;
      const res = await fetchWithAuth(endpoint);
      if (res.ok) {
        const data = await res.json();
        setViewRecordsData(data.records || data);
      }
    } catch (err) {
      console.error('Failed to load records:', err);
    } finally {
      setViewRecordsLoading(false);
    }
  };

  const fmtDate = (v) => { try { return v ? new Date(v).toLocaleDateString() : ''; } catch { return v || ''; } };

  const renderRecordDetail = (rec) => {
    if (rec.description) {
      // SECURITY: rec.description is externally-sourced clinical narrative (FHIR feeds).
      // Render as TEXT only — never dangerouslySetInnerHTML on untrusted HTML (stored-XSS). Rich HTML
      // rendering can return post-beta via a sanitizer (e.g. DOMPurify) once record-import goes live.
      return <div className="hrh-clinicalNarrative">{rec.description}</div>;
    }

    const v = rec.lab_values || {};
    const Field = ({ label, val }) => val && val !== '' && val !== 'no_unit' ? (
      <div className="hrh-recordField"><strong>{label}:</strong> {val}</div>
    ) : null;

    switch (rec.type) {
      case 'medication':
        return (<>
          <Field label="Status" val={rec.active ? 'Active' : 'Discontinued'} />
          <Field label="Dosage" val={rec.dosage} />
          <Field label="Frequency" val={rec.frequency} />
          <Field label="Start" val={fmtDate(rec.start_date)} />
          <Field label="End" val={fmtDate(rec.end_date)} />
          {rec.notes && <Field label="Notes" val={rec.notes} />}
        </>);
      case 'fhir_labs':
        return (<>
          {Object.entries(v).filter(([k]) => !k.startsWith('_')).map(([name, data]) => (
            <div key={name} className="hrh-recordField">
              <strong>{name}:</strong> {typeof data === 'object'
                ? `${data.value != null ? data.value : ''} ${data.unit || ''}${data.reference_range ? ` (ref: ${data.reference_range})` : ''}${data.flag ? ` [${data.flag}]` : ''}`
                : String(data)}
            </div>
          ))}
        </>);
      case 'fhir_vitals': {
        const VITAL_NAMES = { systolic_bp: 'Systolic BP', diastolic_bp: 'Diastolic BP', heart_rate: 'Heart Rate', body_temperature: 'Body Temp', weight_kg: 'Weight', height_cm: 'Height', bmi: 'BMI', respiratory_rate: 'Resp Rate', blood_oxygen: 'SpO2' };
        const VITAL_UNITS = { systolic_bp: 'mmHg', diastolic_bp: 'mmHg', heart_rate: 'bpm', body_temperature: '°F', weight_kg: 'kg', height_cm: 'cm', bmi: '', respiratory_rate: '/min', blood_oxygen: '%' };
        return (
          <div className="hrh-vitalsGrid">
            {Object.entries(v).filter(([k]) => !k.startsWith('_')).map(([name, val]) => (
              <div key={name} className="hrh-vitalCard">
                <span className="hrh-vitalName">{VITAL_NAMES[name] || name.replace(/_/g, ' ')}</span>
                <span className="hrh-vitalValue">{val}</span>
                <span className="hrh-vitalUnit">{VITAL_UNITS[name] || ''}</span>
              </div>
            ))}
          </div>
        );
      }
      default:
        return null;
    }
  };

  const TYPE_ORDER = ['ai_summary', 'allergy_detail', 'condition', 'medication', 'fhir_labs', 'observation', 'fhir_vitals', 'vaccination', 'consultation', 'procedure', 'care_transition', 'diagnostic_report', 'clinical_document', 'provider', 'organization', 'facility', 'ai_citation', 'record_source', 'data_source'];
  const TYPE_LABELS = {
    ai_summary: 'Patient Summary', allergy_detail: 'Allergies', condition: 'Conditions',
    medication: 'Medications', fhir_labs: 'Lab Results', observation: 'Lab Results',
    fhir_vitals: 'Vital Signs', vaccination: 'Immunizations',
    consultation: 'Procedures & Encounters', procedure: 'Procedures',
    care_transition: 'Care Transitions', diagnostic_report: 'Diagnostic Reports',
    clinical_document: 'Clinical Documents', provider: 'Care Team',
    organization: 'Organizations', facility: 'Facilities',
    ai_citation: 'AI Citations', record_source: 'Record Sources', data_source: 'Data Sources',
  };

  const groupRecordsByType = (recs) => {
    const groups = {};
    (recs || []).forEach(r => {
      const type = r.type || 'other';
      if (!groups[type]) groups[type] = [];
      groups[type].push(r);
    });
    const sorted = [];
    TYPE_ORDER.forEach(t => { if (groups[t]) { sorted.push([t, groups[t]]); delete groups[t]; } });
    Object.entries(groups).forEach(([t, recs]) => sorted.push([t, recs]));
    return sorted;
  };

  const handlePathClick = (path) => {
    if (path.comingSoon) return;
    const viewActions = ['view-mobile-health', 'view-medicare', 'view-upload'];
    if (viewActions.includes(path.action)) {
      setActiveView(path.action.replace('view-', ''));
    }
  };

  const totalRecords = connections.reduce((sum, c) => sum + (c.total_records || 0), 0);
  const activeConns = connections.filter(c => c.connection_status === 'active');

  // =========================================================================
  // Detail Views
  // =========================================================================

  if (activeView === 'mobile-health') {
    const instructions = getRecordsConnectionInstructions();
    const appStoreUrl = getHHSAppStoreUrl();
    return (
      <div className="hrh-card">
        <button className="hrh-back" onClick={() => setActiveView('hub')}>
          &larr; Back to Health Records Hub
        </button>
        <div className="hrh-info">
          <span className="hrh-infoIcon">{'\uD83D\uDCF1'}</span>
          <h3 className="hrh-detailTitle">Sync from Mobile Health Platform</h3>
          <p className="hrh-detailDesc">
            Import clinical records (conditions, medications, lab results, immunizations, procedures,
            allergies) from your hospitals, labs, and clinics via HealistHealthSync.
          </p>
          <p className="hrh-detailDesc">
            HealistHealthSync reads clinical records from {isIOS ? 'Apple Health' : isAndroid ? 'Google Health Connect' : 'Apple Health (iOS) or Google Health Connect (Android)'}
            {' '}and syncs them to Healist automatically every 2 hours.
          </p>
          <div className="hrh-howTo">
            <h4 className="hrh-howToTitle">How to Connect</h4>
            <ol className="hrh-howToSteps">
              {instructions.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>
          <div className="hrh-detailActions" style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
            {(isIOS || !isMobile) && (
              <a href="https://apps.apple.com/app/healist-health-sync/id6742478681"
                 target="_blank" rel="noopener noreferrer"
                 className="btn btn-primary" style={{textDecoration: 'none'}}>
                Get HealistHealthSync (iOS)
              </a>
            )}
            {(isAndroid || !isMobile) && (
              <button className="btn btn-outline" disabled>
                Google Play (Coming Soon)
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (activeView === 'upload') {
    return (
      <div className="hrh-card">
        <button className="hrh-back" onClick={() => setActiveView('hub')}>
          &larr; Back to Health Records Hub
        </button>
        <div className="hrh-info">
          <span className="hrh-infoIcon">{'\uD83D\uDCC4'}</span>
          <h3 className="hrh-detailTitle">Upload Medical Records</h3>
          <p className="hrh-detailDesc">
            Upload any medical document &mdash; lab results, discharge summaries, imaging reports
            &mdash; and our AI will extract conditions, medications, allergies, and lab values
            automatically.
          </p>
          <p className="hrh-detailDesc">
            Supported formats: PDF, JPEG, PNG, TIFF, Word documents, and text files (up to 50 MB).
          </p>
          <div className="hrh-detailActions">
            <button className="btn btn-primary" onClick={() => window.dispatchEvent(new CustomEvent('navigate-subtab', { detail: 'files' }))}>
              Go to Medical Files
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (activeView === 'medicare') {
    return (
      <div className="hrh-card">
        <button className="hrh-back" onClick={() => setActiveView('hub')}>
          &larr; Back to Health Records Hub
        </button>
        <div className="hrh-info">
          <span className="hrh-infoIcon">{'\uD83C\uDFDB\uFE0F'}</span>
          <h3 className="hrh-detailTitle">Connect Medicare</h3>
          <p className="hrh-detailDesc">
            Import your complete Medicare claims history using the CMS Blue Button 2.0 API.
            This includes all claims, diagnoses, procedures, provider visits, and coverage
            information from your Medicare record.
          </p>
          {isDev && (
            <div className="hrh-sandboxHint">
              <strong>Sandbox Mode</strong>
              <p className="hrh-sandboxHintText">
                Click Connect, then choose Medicare.gov login and enter:
                <code>BBUser00000</code> / <code>PW00000!</code>
              </p>
            </div>
          )}
          <div className="hrh-detailActions">
            <button
              className="btn btn-primary"
              onClick={handleMedicareConnect}
            >
              Connect to Medicare
            </button>
          </div>
        </div>
      </div>
    );
  }

  // =========================================================================
  // Hub View (default)
  // =========================================================================

  return (
    <div className="hrh-card">
      <div className="hrh-header">
        <h3 className="hrh-headerTitle">Connect Your Health Records</h3>
        <span className="hrh-headerStatus">
          {loading ? '...' : `${activeConns.length} source${activeConns.length !== 1 ? 's' : ''} connected`}
        </span>
      </div>

      <div className="hrh-body">
        <div className="hrh-paths">
          {PATHS.map((path) => (
            <button
              key={path.id}
              className="hrh-pathCard"
              onClick={() => handlePathClick(path)}
              disabled={path.comingSoon}
              style={path.comingSoon ? { opacity: 0.6, cursor: 'default' } : undefined}
            >
              <span className="hrh-pathIcon">{path.icon}</span>
              <div className="hrh-pathText">
                <span className="hrh-pathTitle">
                  {path.title}
                  {path.subtitle && (
                    <span className="hrh-pathSubtitle">{path.subtitle}</span>
                  )}
                  {path.comingSoon ? (
                    <span className="hrh-pathBadge" style={{ background: '#F3F4F6', color: '#6B7280' }}>Coming Soon</span>
                  ) : path.badge && (
                    <span className={`hrh-pathBadge ${path.badgeClass}`}>{path.badge}</span>
                  )}
                </span>
                <span className="hrh-pathDesc">{path.desc}</span>
              </div>
              <span className="hrh-pathArrow">&rsaquo;</span>
            </button>
          ))}
        </div>

        {!loading && connections.filter(c => c.connection_status === 'active').length > 0 && (
          <div className="hrh-sources">
            <h4 className="hrh-sourcesTitle">Connected Sources</h4>
            <p className="hrh-sourcesNote">Some sources may take 5-15 mins to complete download/sync. Record count will update as soon as sync is completed.</p>
            {connections.filter(c => c.connection_status === 'active').map((conn) => {
              const providerNames = (conn.providers || [])
                .map(p => p.name || p.brand_id)
                .filter(Boolean);
              const displayName = providerNames.length > 0
                ? providerNames.join(', ')
                : conn.ehr_name || conn.ehr_system;
              return (
                <div key={conn.id} className="hrh-sourceRow">
                  <div className="hrh-sourceInfo">
                    <span className="hrh-sourceName">
                      {displayName}
                      <span className={`hrh-sourceBadge hrh-sourceBadge--${conn.connection_status}`}>
                        {conn.connection_status}
                      </span>
                    </span>
                    <span className="hrh-sourceCount">
                      {conn.total_records} record{conn.total_records !== 1 ? 's' : ''}
                      {conn.last_sync_at && (
                        <span className="hrh-sourceSyncDate">
                          {' · synced '}
                          {new Date(conn.last_sync_at).toLocaleDateString()}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="hrh-sourceActions">
                    {conn.total_records > 0 ? (
                      <button className="btn btn-secondary btn-sm" onClick={() => handleViewRecords(conn)}>
                        View Records
                      </button>
                    ) : null}
                    {conn.connection_status === 'active' && (
                      <>
                        {SYNCABLE_SOURCES[conn.source_type] && (
                          <button
                            className="btn btn-primary btn-sm"
                            disabled={syncingId === conn.id}
                            onClick={() => handleSync(conn.id, conn.source_type)}
                          >
                            {syncingId === conn.id ? 'Syncing...' : 'Sync Now'}
                          </button>
                        )}
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => { setDisconnectConn(conn); setDisconnectOption('retain'); }}
                        >
                          Disconnect
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
            <div className="hrh-total">
              {totalRecords} total record{totalRecords !== 1 ? 's' : ''} imported
            </div>
          </div>
        )}
      </div>

      {disconnectConn && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setDisconnectConn(null)}>
          <div className="modal-content">
            <div className="modal-header">
              <h3>Disconnect — {disconnectConn.ehr_name || disconnectConn.ehr_system}</h3>
              <button className="hsm-closeBtn" onClick={() => setDisconnectConn(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="hrh-disconnectOptions">
                <label className="hrh-radioOption">
                  <input type="radio" name="disconnectAction" value="retain"
                    checked={disconnectOption === 'retain'}
                    onChange={() => setDisconnectOption('retain')} />
                  <div>
                    <strong>Disconnect but KEEP all imported data</strong>
                    <p>Your imported health records will remain in your health context.</p>
                  </div>
                </label>
                <label className="hrh-radioOption">
                  <input type="radio" name="disconnectAction" value="delete"
                    checked={disconnectOption === 'delete'}
                    onChange={() => setDisconnectOption('delete')} />
                  <div>
                    <strong>Disconnect AND DELETE all imported records</strong>
                    <p>All records from this source will be permanently removed from your health context.</p>
                  </div>
                </label>
              </div>
              <div className="hrh-detailActions">
                <button className="btn btn-primary" onClick={executeDisconnect}>Execute</button>
                <button className="btn btn-secondary" onClick={() => setDisconnectConn(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewRecordsConn && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setViewRecordsConn(null)}>
          <div className="modal-content modal-large">
            <div className="modal-header">
              <h3>Imported Records — {viewRecordsConn.ehr_name || viewRecordsConn.ehr_system}</h3>
              <button className="hsm-closeBtn" onClick={() => setViewRecordsConn(null)}>&times;</button>
            </div>
            <div className="modal-body hrh-recordsModal">
              {viewRecordsData && viewRecordsData.length > 0 && (
                <div className="hrh-expandAllRow">
                  <span className="hrh-recordCount">{viewRecordsData.length} records</span>
                  <button className="btn btn-secondary btn-sm" onClick={() => {
                    if (allExpanded) {
                      setExpandedRecords(new Set());
                      setAllExpanded(false);
                    } else {
                      setExpandedRecords(new Set(viewRecordsData.map((r, i) => `${r.source}-${r.id || i}`)));
                      setAllExpanded(true);
                    }
                  }}>
                    {allExpanded ? 'Collapse All' : 'Expand All'}
                  </button>
                </div>
              )}
              {viewRecordsLoading ? (
                <div className="hrh-searchingAnim"><div className="hrh-spinner"></div><p>Loading records...</p></div>
              ) : !viewRecordsData || viewRecordsData.length === 0 ? (
                <p className="hrh-noResults">No imported records found for this source.</p>
              ) : (
                <div className="hrh-recordsSections">
                  {groupRecordsByType(viewRecordsData).map(([type, recs]) => (
                    <div key={type} className="hrh-recSection">
                      <div className="hrh-recSectionHeader">
                        <h4 className="hrh-recSectionTitle">{TYPE_LABELS[type] || type}</h4>
                        <span className="hrh-recSectionCount">{recs.length}</span>
                      </div>
                      <div className="hrh-recSectionBody">
                        {recs.map((rec, i) => {
                          const key = `${rec.source}-${rec.id || i}`;
                          const isExpanded = expandedRecords.has(key);
                          return (
                            <div key={key} className={`hrh-recordItem ${isExpanded ? 'hrh-recordItem--expanded' : ''}`}
                              onClick={() => setExpandedRecords(prev => {
                                const next = new Set(prev);
                                if (next.has(key)) next.delete(key); else next.add(key);
                                return next;
                              })}>
                              <div className="hrh-recordHeader">
                                <span className="hrh-recordTitle">{rec.title || ''}</span>
                                <div className="hrh-recordMeta">
                                  {rec.active === false && <span className="hrh-badge hrh-badge--inactive">Discontinued</span>}
                                  {rec.active === true && <span className="hrh-badge hrh-badge--active">Active</span>}
                                  {rec.date && <span className="hrh-recordDate">{new Date(rec.date).toLocaleDateString()}</span>}
                                </div>
                              </div>
                              {isExpanded && (
                                <div className="hrh-recordExpanded">
                                  {renderRecordDetail(rec)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
